import { setCookie, getCookie, deleteCookie } from 'cookies-next';
import { decrypt, encrypt } from './cryptoMethods';
import moment from 'moment';

interface UserData {
    user?: any;
    accessToken?: string;
    refreshToken?: string;
    fireBaseToken?: string;
}

// Check if running in production (HTTPS) or development (HTTP)
const isProduction = process.env.NODE_ENV === 'production';

// Cookie options for persistent cookies (365 days)
// IMPORTANT: Must include maxAge, path, and sameSite to prevent session cookie behavior
export const cookieOptions = {
    maxAge: 31536000, // 365 days in seconds
    path: '/', // Available across entire site
    httpOnly: false,
    secure: isProduction, // Only use secure flag in production (HTTPS)
    sameSite: 'lax' as const, // Required by modern browsers
};

const setOnlyUser = ({ user}: UserData): void => {
    setCookie('user', encrypt(JSON.stringify(user), process.env.SECRET_KEY), cookieOptions);
};


const setCookieUser = ({ user, accessToken, refreshToken, fireBaseToken }: UserData): void => {
    accessToken && setCookie('_access', encrypt(JSON.stringify(accessToken), process.env.SECRET_KEY), cookieOptions);
    setCookie('_refresh', encrypt(JSON.stringify(refreshToken), process.env.SECRET_KEY), cookieOptions);
    setCookie('_fb_token', encrypt(JSON.stringify(fireBaseToken), process.env.SECRET_KEY), cookieOptions);
    setCookie('user', encrypt(JSON.stringify(user), process.env.SECRET_KEY), cookieOptions);
};

const setAuthIgnoreCookie = (COOKIE_NAME: string, hours: number) => {
    const now = new Date();
    const expiry = new Date(now.getTime() + hours * 60 * 60 * 1000);
    const encryptedExpiry = encrypt(JSON.stringify(expiry), process.env.SECRET_KEY);
    setCookie(COOKIE_NAME, encryptedExpiry, { expires: expiry });
};

const clearUserCookies = (): void => {
    deleteCookie('_access', { httpOnly: false, secure: isProduction });
    deleteCookie('_refresh', { httpOnly: false, secure: isProduction });
    deleteCookie('_fb_token', { httpOnly: false, secure: isProduction });
    deleteCookie('user', { httpOnly: false, secure: isProduction });
};

// 세션 토큰만 지우고 소셜 인증 토큰(_fb_token)은 남긴다.
// 회원가입은 소셜 로그인으로 받은 _fb_token 을 input_token 으로 보내 완료되는데, 프로필 설정 화면이
// 부르는 인증 API 가 401 이 나면(가입 전이라 정상이다) 토큰 갱신이 실패해 세션 정리가 돈다.
// 그때 _fb_token 까지 지우면 가입 제출이 "로그인 토큰이 만료되었습니다" 로 끝나 가입 자체가 불가능해진다
// (2026-09-16 실서버에서 신규 사용자가 닉네임을 바꿔가며 네 번 시도하다 매번 로그인 화면으로 튕겼다).
// 로그아웃 버튼처럼 사용자가 의도한 로그아웃은 clearUserCookies 로 전부 지운다.
const clearSessionCookies = (): void => {
    deleteCookie('_access', { httpOnly: false, secure: isProduction });
    deleteCookie('_refresh', { httpOnly: false, secure: isProduction });
    deleteCookie('user', { httpOnly: false, secure: isProduction });
};

const clearAuthIgnoreCookie = (COOKIE_NAME: string) => {
    deleteCookie(COOKIE_NAME);
};

const deleteCookieAlert = () => {
    const today = moment();
    const expires = moment().add(1, 'day').startOf('day');
    setCookie('badgeChallengeAlertClosed', today.format('YYYY-MM-DD'), { expires: expires.toDate() });
};

const updateCookie = ({ accessToken, user }: UserData): void => {
    setCookie('_access', encrypt(JSON.stringify(accessToken), process.env.SECRET_KEY), cookieOptions);
    setCookie('user', encrypt(JSON.stringify(user), process.env.SECRET_KEY), cookieOptions);
};

const setExpiredToken = (expired_at: any) => {
    setCookie('expiredAtJoyToken', encrypt(JSON.stringify(expired_at), process.env.SECRET_KEY), cookieOptions);
};

const deleteExpiredToken = (req?: any, res?: any) => {
    deleteCookie('expiredAtJoyToken', { req, res });
};

const getExpiredToken = () => {
    return decrypt(getCookie('expiredAtJoyToken') || '', process.env.SECRET_KEY);
};

const deleteCookieUser = (req?: any, res?: any) => {
    deleteCookie('_access', { req, res });
    deleteCookie('_refresh', { req, res });
    deleteCookie('_fb_token', { req, res });
    deleteCookie('user', { req, res });
};

const getCookieUser = (): string => {
    const fb_token = decrypt(getCookie('_fb_token') || '', process.env.SECRET_KEY);
    return fb_token;
};

const getAccessToken = (): string => {
    const rawAccess = getCookie('_access');
    if (!rawAccess) return '';
    
    try {
        const decrypted = decrypt(rawAccess as string, process.env.SECRET_KEY);
        if (!decrypted) return '';
        
        try {
            const parsed = JSON.parse(decrypted);
            const token = typeof parsed === 'string' ? parsed : decrypted;
            return token.replace(/^"|"$/g, '').trim();
        } catch (e) {
            return decrypted.replace(/^"|"$/g, '').trim();
        }
    } catch (e) {
        return '';
    }
};

function isTokenExpired(expiresAt: number): boolean {
    return Date.now() >= expiresAt;
}

export {
    setCookieUser,
    setAuthIgnoreCookie,
    getExpiredToken,
    getCookieUser,
    setExpiredToken,
    getAccessToken,
    deleteCookieUser,
    updateCookie,
    isTokenExpired,
    clearUserCookies,
    clearSessionCookies,
    deleteCookieAlert,
    clearAuthIgnoreCookie,
    deleteExpiredToken,
    setOnlyUser
};
